import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import PaymentExportVM from './PaymentExportVM';
import { observer } from 'mobx-react';
import PaymentExportLookup from '../../Models/PaymentExport/Lookups/PaymentExportLookup';
import NewPaymentExportCommand from '../../Models/PaymentExport/Lookups/NewPaymentExportCommand';
import * as Roles from '../../Models/Security/Roles';
import PaymentExportDetailLookup from '../../Models/PaymentExport/Lookups/PaymentExportDetailLookup';
import { EnumHelper, Misc, ModalUtils, Model } from '@singularsystems/neo-core';
import { PaymentType } from '../../Models/PaymentExport/PaymentType';

@observer
export default class PaymentExportView extends Views.ViewBase<PaymentExportVM> {

    constructor(props: unknown) {
        super("Payment Export", PaymentExportVM, props);
    }

    public render() {
        const canEdit = this.viewModel.authorisation.hasRole(Roles.PaymentExport.Edit);

        return (
            <div className="mt-4 mb-4">
                {!this.viewModel.currentExport &&
                    <>
                        <Neo.Card title="Criteria">
                            <div className="row">
                                <div className="col-lg-3">
                                    <Neo.FormGroupInline bind={this.viewModel.exportCriteria.meta.entityId}
                                        select={{ itemSource: this.viewModel.dataCache.entities }}
                                        label="Entity" />
                                </div>
                                <div className="col-lg-3">
                                    <Neo.FormGroupInline bind={this.viewModel.exportCriteria.meta.startDate} />
                                </div>
                                <div className="col-lg-3">
                                    <Neo.FormGroupInline bind={this.viewModel.exportCriteria.meta.endDate} />
                                </div>
                                <div className="col-lg-3">
                                    <Neo.FormGroupInline bind={this.viewModel.exportCriteria.meta.paymentType} select={{ items: this.viewModel.paymentTypes }} />
                                </div>
                            </div>
                        </Neo.Card>
                        <Neo.Card title="Payment Exports">
                            <div style={{ textAlign: "right" }} className="mb-3">
                                {canEdit && <Neo.Button onClick={() => this.beginAddNewExport()}>
                                    New Payment Export
                                </Neo.Button>}
                            </div>
                            <Neo.Pager pageManager={this.viewModel.pageManager} pageControls="bottom" >
                                <NeoGrid.Grid<PaymentExportLookup>>
                                    {(paymentExport, meta) => (
                                        <NeoGrid.Row>
                                            <NeoGrid.Column display={meta.paymentDate} />
                                            <NeoGrid.Column display={meta.entityId}
                                                select={{ itemSource: this.viewModel.dataCache.entities, renderAsText: true }}
                                                label="Entity" />
                                            <NeoGrid.Column display={meta.paymentType} select={{ items: this.viewModel.paymentTypes, renderAsText: true }} />
                                            <NeoGrid.Column display={meta.paymentAmount} />
                                            <NeoGrid.Column display={meta.isExported} />
                                            <NeoGrid.ButtonColumn
                                                editButton={{
                                                    text: "View",
                                                    icon: "arrow-right",
                                                    iconAlignment: "right",
                                                    onClick: () => this.viewModel.loadPaymentExport(paymentExport.paymentExportId)
                                                }}
                                                deleteButton={{
                                                    onClick: () => this.confirmDelete(paymentExport,
                                                        "Are you sure you want to delete this batch and all linked payments?",
                                                        () => this.viewModel.deletePaymentExport(paymentExport)),
                                                    disabled: !canEdit
                                                }} />
                                        </NeoGrid.Row>
                                    )}
                                </NeoGrid.Grid>
                            </Neo.Pager>
                        </Neo.Card>
                    </>}

                {this.viewModel.currentExport && this.renderPaymentExport(this.viewModel.currentExport)}

                <Neo.Modal
                    title="New Payment Export"
                    bindModel={this.viewModel.meta.newPaymentExport}
                    acceptButton={{ onClick: () => this.viewModel.createNewPaymentExport() }}
                    onClose={() => this.viewModel.resetNewPayment()}>
                    {(paymentExport: NewPaymentExportCommand) => (
                        <div>
                            <Neo.FormGroupInline bind={paymentExport.meta.entityId}
                                select={{ itemSource: this.viewModel.dataCache.entities }}
                                label="Entity" />
                            <Neo.FormGroupInline bind={paymentExport.meta.paymentType} select={{ items: this.viewModel.paymentTypes }} readOnly/>
                            <Neo.FormGroupInline bind={paymentExport.meta.startDate} />
                            <Neo.FormGroupInline bind={paymentExport.meta.endDate} />
                        </div>
                    )}
                </Neo.Modal>
            </div>
        );
    }

    private renderPaymentExport(paymentExport: PaymentExportLookup) {
        const canExport = this.viewModel.authorisation.hasRole(Roles.PaymentExport.Export);
        const canEdit = this.viewModel.authorisation.hasRole(Roles.PaymentExport.Edit);

        return (
            <>
                <Neo.Card title="Batch Details">
                    <Neo.GridLayout justifyContent="between">
                        <div className="mb-3">
                            <Neo.Button icon="arrow-left" variant="light" onClick={() => this.viewModel.back()}>
                                Back
                            </Neo.Button>
                        </div>
                        <div style={{ textAlign: "right" }} >
                            <Neo.Button
                                disabled={!canExport}
                                onClick={() => this.export()} icon="download">
                                {paymentExport.isExported ? "Re-Export" : "Export"}
                            </Neo.Button>
                        </div>
                    </Neo.GridLayout>
                    <div className="row">
                        <div className="col-lg-3">
                            <Neo.FormGroupInline display={paymentExport.meta.entityId}
                                select={{ itemSource: this.viewModel.dataCache.entities }}
                                label="Entity" />
                        </div>
                        <div className="col-lg-3">
                            <Neo.FormGroupInline display={paymentExport.meta.paymentType} select={{ items: this.viewModel.paymentTypes }} />
                        </div>
                        <div className="col-lg-3">
                            <Neo.FormGroupInline display={paymentExport.meta.paymentAmount} />
                        </div>
                        <div className="col-lg-3">
                            <Neo.FormGroupInline display={paymentExport.meta.paymentDate} />
                        </div>
                    </div>

                    {paymentExport.unProcessedCount > 0 && paymentExport.paymentType === PaymentType.Dividend &&
                        <Neo.Alert variant="warning" icon="exclamation-triangle" className="mt-2">Note: There are {paymentExport.unProcessedCount} unprocessed dividends on this date. Please check the dividend processing screen.</Neo.Alert>}

                    {paymentExport.unlinkedRecordCount > 0 &&
                        <Neo.Alert variant="warning" className="mt-2">
                            Note: There are {paymentExport.unlinkedRecordCount} record(s) on this date not linked to a payment batch.

                            <div className="mt-2">
                                <Neo.Button onClick={() => this.viewModel.addMissingRecords(paymentExport)}>
                                    {paymentExport.isExported ? "Add records to new batch" : "Add records to this batch"}
                                </Neo.Button>
                            </div>
                        </Neo.Alert>}
                </Neo.Card>
                <Neo.Card title="Payment Export Details">
                    <Neo.Pager pageManager={this.viewModel.detailsPageManager} pageControls="bottom" >
                        <NeoGrid.Grid<PaymentExportDetailLookup>>
                            {(detail, meta) => (
                                <NeoGrid.Row>
                                    <NeoGrid.Column display={meta.myStatementDescription} />
                                    <NeoGrid.Column display={meta.beneficiaryAccountNumber} />
                                    <NeoGrid.Column display={meta.beneficiaryName} />
                                    <NeoGrid.Column display={meta.amount} />
                                    <NeoGrid.Column display={meta.statusText} />
                                    <NeoGrid.Column display={meta.investmentRefNo} />
                                    <NeoGrid.Column display={meta.investorName} />
                                    <NeoGrid.Column display={meta.recordIsAuthorised} label="Record Authorised?" />
                                    <NeoGrid.ButtonColumn
                                        deleteButton={{
                                            onClick: () => this.confirmDelete(this.viewModel.currentExport!,
                                                "Are you sure you want to remove this record from the batch?", 
                                                () => this.viewModel.deletePaymentExportDetail(detail)),
                                            disabled: !canEdit
                                        }} />
                                </NeoGrid.Row>
                            )}
                        </NeoGrid.Grid>
                    </Neo.Pager>
                </Neo.Card>
            </>
        )
    }

    private export() {

        if (this.viewModel.currentExport) {
            const body = <div>
                <p>
                    <i className='fa fa-user-shield text-danger' />
                    <span>
                        There may be <strong>personal information</strong> in this export, once downloaded please move it to a secure location on your computer and password protect it before sending it anywhere.
                    </span>
                </p>

                {this.viewModel.currentExport.isExported &&
                    <Neo.Alert className="mt-3" variant="warning">
                        Warning: These payment details have already been exported. Are you sure you want to export them again?
                    </Neo.Alert>}
            </div>

            ModalUtils.showYesNoDismissible("Payment Export Download", body, () => this.viewModel.export());
        }
    }

    private async beginAddNewExport() {
        const paymentTypeProperty = Model.ObservableProperty.required("Payment Type", null);

        const result = await ModalUtils.showOkCancel(
            "New Payment Export",
            <Neo.FormGroup bind={paymentTypeProperty} select={{ items: this.viewModel.paymentTypes }} />,
            paymentTypeProperty);

        if (result === Misc.ModalResult.Yes) {
            this.viewModel.addNewPaymentExport(paymentTypeProperty.value);
        }
    }

    private confirmDelete(batch: PaymentExportLookup, prompt: string, onOk: () => void) {
        let warning;

            if (batch.isExported) {
                warning = <Neo.Alert variant="warning">Note: This batch has already been exported.</Neo.Alert>
            }

            ModalUtils.showYesNoDismissible("Delete", <div>
                <p>{prompt}</p>
                {warning}
                </div>, onOk);
    }
}